/**
 * TaxiEditorPanel — the authoring surface for the taxi waypoint editor.
 *
 * Sections: version switcher (+ Save / Duplicate / New), group list with
 * activation windows, a tool palette (Select / Add node / Add edge / Close
 * segment), the selected node/edge properties form (role, heading, throughput,
 * group, activation window, custom-JSON flags, edge closures + emergency close),
 * and a per-exit departure distribution readout.
 *
 * It mutates the draft plan via `taxiEditorStore` (live for routing) and flushes
 * to the DB via `useTaxiPlanMutations`.
 */
import { useState } from 'react'
import { Plus, Save, Copy, Trash2, MousePointer2, Circle, Spline, Ban, X, Wand2, Plane } from 'lucide-react'
import { useTaxiPlanMutations } from '@/hooks/useTaxiPlans'
import {
  selectSelectedEdge,
  selectSelectedNode,
  selectSelectedRunway,
  useTaxiEditorStore,
  type EditorMode,
} from '../store/taxiEditorStore'
import type {
  ActiveWindow,
  EdgeRole,
  NodeRole,
  TaxiNode,
  TaxiPlan,
} from '../data/taxiGraph'

const NODE_ROLES: NodeRole[] = ['taxi', 'apron', 'hold_short', 'runway_exit', 'exit_to_taxiway', 'gate']
const EDGE_ROLES: EdgeRole[] = ['taxiway', 'apron_lead', 'runway', 'exit', 'temporary']
const GROUP_ROLES = ['permanent', 'temporary', 'emergency_reroute'] as const

interface Props {
  plans: TaxiPlan[]
  activePlanId: string | null
  onSelectPlan: (id: string) => void
  /** exit_to_taxiway nodes with how many departures route through them */
  distribution: Array<{ node: TaxiNode; count: number }>
  /** when provided, renders an Exit button (used by the Layout Designer mount) */
  onClose?: () => void
  /** when provided, renders a "Regenerate from real geometry" action (event planner mount) */
  onRegenerateFromGeometry?: () => void
}

// ── datetime-local helpers ───────────────────────────────────────────────────
const isoToLocal = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const localToIso = (local: string) => (local ? new Date(local).toISOString() : null)

const field = 'w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-xs text-white/90'
const label = 'text-[10px] font-semibold uppercase tracking-wide text-white/40'
const sectionTitle = 'text-[10px] font-bold uppercase tracking-widest text-white/30'

function ActiveWindowEditor({
  value,
  onChange,
  showEnabled = true,
}: {
  value: ActiveWindow
  onChange: (w: ActiveWindow) => void
  showEnabled?: boolean
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <select
          className={field}
          value={value.mode}
          onChange={(e) => onChange({ ...value, mode: e.target.value as ActiveWindow['mode'] })}
        >
          <option value="always">Always active</option>
          <option value="window">Active in window</option>
        </select>
        {showEnabled && (
          <label className="flex items-center gap-1 whitespace-nowrap text-xs text-white/70">
            <input
              type="checkbox"
              checked={value.enabled}
              onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
            />
            On
          </label>
        )}
      </div>
      {value.mode === 'window' && (
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <div className={label}>From</div>
            <input
              type="datetime-local"
              className={field}
              value={isoToLocal(value.from)}
              onChange={(e) => onChange({ ...value, from: localToIso(e.target.value) })}
            />
          </div>
          <div>
            <div className={label}>Until</div>
            <input
              type="datetime-local"
              className={field}
              value={isoToLocal(value.until)}
              onChange={(e) => onChange({ ...value, until: localToIso(e.target.value) })}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function JsonFlagsEditor({
  value,
  onChange,
}: {
  value: Record<string, unknown>
  onChange: (v: Record<string, unknown>) => void
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2))
  const [err, setErr] = useState<string | null>(null)
  return (
    <div className="space-y-1">
      <div className={label}>Custom JSON flags</div>
      <textarea
        className={`${field} h-20 font-mono`}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          try {
            const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : {}
            setErr(null)
            onChange(parsed)
          } catch {
            setErr('Invalid JSON')
          }
        }}
      />
      {err && <div className="text-[10px] text-rose-400">{err}</div>}
    </div>
  )
}

const TOOLS: Array<{ mode: EditorMode; icon: typeof Circle; label: string }> = [
  { mode: 'view', icon: MousePointer2, label: 'Select' },
  { mode: 'place_node', icon: Circle, label: 'Node' },
  { mode: 'place_edge', icon: Spline, label: 'Edge' },
  { mode: 'place_runway', icon: Plane, label: 'Runway' },
  { mode: 'close_segment', icon: Ban, label: 'Close' },
]

export function TaxiEditorPanel({
  plans,
  activePlanId,
  onSelectPlan,
  distribution,
  onClose,
  onRegenerateFromGeometry,
}: Props) {
  const draftPlan = useTaxiEditorStore((s) => s.draftPlan)
  const dirty = useTaxiEditorStore((s) => s.dirty)
  const mode = useTaxiEditorStore((s) => s.mode)
  const placingRole = useTaxiEditorStore((s) => s.placingRole)
  const activeGroupId = useTaxiEditorStore((s) => s.activeGroupId)
  const selectedNode = useTaxiEditorStore(selectSelectedNode)
  const selectedEdge = useTaxiEditorStore(selectSelectedEdge)
  const selectedRunway = useTaxiEditorStore(selectSelectedRunway)
  const {
    setMode,
    setPlacingRole,
    setActiveGroup,
    addGroup,
    updateGroup,
    deleteGroup,
    updateNode,
    updateEdge,
    addClosure,
    removeClosure,
    updateRunway,
    updateRunwayEnd,
    setActiveRunway,
    deleteSelected,
    loadPlan,
    markSaved,
  } = useTaxiEditorStore()

  const { save, duplicate } = useTaxiPlanMutations()

  if (!draftPlan) return null

  const onSave = async () => {
    const saved = await save.mutateAsync(draftPlan)
    loadPlan(saved)
    markSaved()
    onSelectPlan(saved.id)
  }
  const onDuplicate = async () => {
    const name = window.prompt('New version name', `${draftPlan.name} copy`)
    if (!name) return
    const saved = await duplicate.mutateAsync({ source: draftPlan, name })
    onSelectPlan(saved.id)
  }

  return (
    <div className="pointer-events-auto absolute right-4 top-4 bottom-4 z-[8] flex w-[360px] flex-col gap-3 overflow-y-auto rounded-xl border border-white/10 bg-slate-950/85 p-3 text-white backdrop-blur-md">
      {/* Version switcher */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className={sectionTitle}>Plan / Version</div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1 rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-white/60 hover:bg-white/10 hover:text-white"
              title="Exit taxiway editor"
            >
              <X className="h-3 w-3" /> Exit
            </button>
          )}
        </div>
        <select
          className={field}
          value={activePlanId ?? ''}
          onChange={(e) => onSelectPlan(e.target.value)}
        >
          {plans.length === 0 && <option value="">{draftPlan.name} (unsaved)</option>}
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.isBaseline ? ' · baseline' : ''}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onSave}
            disabled={save.isPending}
            className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-semibold ${
              dirty ? 'bg-sky-500/30 text-sky-100 hover:bg-sky-500/40' : 'bg-white/5 text-white/40'
            }`}
          >
            <Save className="h-3.5 w-3.5" />
            {save.isPending ? 'Saving…' : dirty ? 'Save *' : 'Saved'}
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            className="flex items-center gap-1 rounded bg-white/5 px-2 py-1.5 text-xs text-white/70 hover:bg-white/10"
            title="Duplicate as new version"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
        {onRegenerateFromGeometry && (
          <button
            type="button"
            onClick={() => {
              if (dirty && !window.confirm('Replace the current draft with a fresh import from real airport geometry? Unsaved edits will be lost.')) return
              onRegenerateFromGeometry()
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded border border-emerald-400/30 bg-emerald-500/10 px-2 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/20"
            title="Rebuild the network from real runway/taxiway geometry, then Save"
          >
            <Wand2 className="h-3.5 w-3.5" /> Regenerate from real geometry
          </button>
        )}
      </div>

      {/* Tool palette */}
      <div className="space-y-1.5">
        <div className={sectionTitle}>Tool</div>
        <div className="grid grid-cols-5 gap-1.5">
          {TOOLS.map((t) => {
            const Icon = t.icon
            const on = mode === t.mode
            return (
              <button
                key={t.mode}
                type="button"
                onClick={() => setMode(t.mode)}
                className={`flex flex-col items-center gap-0.5 rounded border px-1 py-1.5 text-[10px] ${
                  on ? 'border-sky-400/50 bg-sky-500/20 text-sky-100' : 'border-white/10 bg-black/40 text-white/60 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            )
          })}
        </div>
        {mode === 'place_node' && (
          <div>
            <div className={label}>New node role</div>
            <select className={field} value={placingRole} onChange={(e) => setPlacingRole(e.target.value as NodeRole)}>
              {NODE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        )}
        {(mode === 'place_node' || mode === 'place_edge' || mode === 'place_runway') && (
          <div>
            <div className={label}>New element group</div>
            <select className={field} value={activeGroupId ?? ''} onChange={(e) => setActiveGroup(e.target.value)}>
              {draftPlan.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <p className="text-[10px] leading-tight text-white/40">
          {mode === 'place_node' && 'Click the map to drop a node.'}
          {mode === 'place_edge' && 'Click two nodes to connect them.'}
          {mode === 'place_runway' && 'Click two threshold points to draw a runway.'}
          {mode === 'close_segment' && 'Click an edge to toggle it closed (reroutes).'}
          {mode === 'view' && 'Click to select · drag a node/runway end to move · Del removes.'}
        </p>
      </div>

      {/* Groups */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className={sectionTitle}>Groups</div>
          <button
            type="button"
            onClick={() => addGroup('New group')}
            className="flex items-center gap-1 rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70 hover:bg-white/10"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
        {draftPlan.groups.map((g) => (
          <div key={g.id} className="space-y-1 rounded border border-white/10 bg-black/30 p-2">
            <div className="flex items-center gap-1.5">
              <input
                className={field}
                value={g.name}
                onChange={(e) => updateGroup(g.id, { name: e.target.value })}
              />
              {draftPlan.groups.length > 1 && (
                <button type="button" onClick={() => deleteGroup(g.id)} className="text-white/40 hover:text-rose-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <select className={field} value={g.role} onChange={(e) => updateGroup(g.id, { role: e.target.value as typeof GROUP_ROLES[number] })}>
              {GROUP_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <ActiveWindowEditor value={g.active} onChange={(w) => updateGroup(g.id, { active: w })} />
          </div>
        ))}
      </div>

      {/* Selected node properties */}
      {selectedNode && (
        <div className="space-y-1.5 rounded border border-sky-400/30 bg-sky-500/5 p-2">
          <div className={sectionTitle}>Node · {selectedNode.role}</div>
          <div>
            <div className={label}>Role</div>
            <select className={field} value={selectedNode.role} onChange={(e) => updateNode(selectedNode.id, { role: e.target.value as NodeRole })}>
              {NODE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <div className={label}>Heading°</div>
              <input
                type="number"
                className={field}
                value={selectedNode.headingDeg ?? ''}
                onChange={(e) => updateNode(selectedNode.id, { headingDeg: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
            {selectedNode.role === 'exit_to_taxiway' && (
              <div>
                <div className={label}>Throughput/slot</div>
                <input
                  type="number"
                  className={field}
                  value={selectedNode.throughputPerSlot ?? ''}
                  onChange={(e) => updateNode(selectedNode.id, { throughputPerSlot: e.target.value === '' ? null : Number(e.target.value) })}
                />
              </div>
            )}
          </div>
          <div>
            <div className={label}>Group</div>
            <select className={field} value={selectedNode.groupId} onChange={(e) => updateNode(selectedNode.id, { groupId: e.target.value })}>
              {draftPlan.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <ActiveWindowEditor value={selectedNode.active} onChange={(w) => updateNode(selectedNode.id, { active: w })} />
          <JsonFlagsEditor value={selectedNode.flags} onChange={(v) => updateNode(selectedNode.id, { flags: v })} />
          <button type="button" onClick={deleteSelected} className="flex items-center gap-1 text-[10px] text-rose-400 hover:text-rose-300">
            <Trash2 className="h-3 w-3" /> Delete node
          </button>
        </div>
      )}

      {/* Selected edge properties */}
      {selectedEdge && (
        <div className="space-y-1.5 rounded border border-amber-400/30 bg-amber-500/5 p-2">
          <div className="flex items-center justify-between">
            <div className={sectionTitle}>Segment · {selectedEdge.role}</div>
            <span className={`rounded px-1.5 py-0.5 text-[10px] ${selectedEdge.active.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>
              {selectedEdge.active.enabled ? 'OPEN' : 'CLOSED'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <div className={label}>Role</div>
              <select className={field} value={selectedEdge.role} onChange={(e) => updateEdge(selectedEdge.id, { role: e.target.value as EdgeRole })}>
                {EDGE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className={label}>Weight ×</div>
              <input
                type="number"
                step="0.1"
                className={field}
                value={selectedEdge.weightMult ?? 1}
                onChange={(e) => updateEdge(selectedEdge.id, { weightMult: Number(e.target.value) || 1 })}
              />
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-white/70">
            <input
              type="checkbox"
              checked={selectedEdge.directed}
              onChange={(e) => updateEdge(selectedEdge.id, { directed: e.target.checked })}
            />
            One-way (A → B)
          </label>
          <button
            type="button"
            onClick={() => updateEdge(selectedEdge.id, { active: { ...selectedEdge.active, enabled: !selectedEdge.active.enabled } })}
            className={`w-full rounded px-2 py-1.5 text-xs font-semibold ${
              selectedEdge.active.enabled ? 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30' : 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30'
            }`}
          >
            {selectedEdge.active.enabled ? 'Emergency close (reroute)' : 'Reopen segment'}
          </button>
          <ActiveWindowEditor value={selectedEdge.active} onChange={(w) => updateEdge(selectedEdge.id, { active: w })} />
          {/* Scheduled closures */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className={label}>Scheduled closures</div>
              <button
                type="button"
                onClick={() => {
                  const from = new Date().toISOString()
                  const until = new Date(Date.now() + 3600_000).toISOString()
                  addClosure(selectedEdge.id, { from, until })
                }}
                className="text-[10px] text-white/60 hover:text-white"
              >
                + add
              </button>
            </div>
            {selectedEdge.closures.map((c) => (
              <div key={c.id} className="flex items-center gap-1">
                <input
                  type="datetime-local"
                  className={field}
                  value={isoToLocal(c.from)}
                  onChange={(e) => {
                    const iso = localToIso(e.target.value)
                    if (iso) {
                      removeClosure(selectedEdge.id, c.id)
                      addClosure(selectedEdge.id, { from: iso, until: c.until, reason: c.reason })
                    }
                  }}
                />
                <input
                  type="datetime-local"
                  className={field}
                  value={isoToLocal(c.until)}
                  onChange={(e) => {
                    const iso = localToIso(e.target.value)
                    if (iso) {
                      removeClosure(selectedEdge.id, c.id)
                      addClosure(selectedEdge.id, { from: c.from, until: iso, reason: c.reason })
                    }
                  }}
                />
                <button type="button" onClick={() => removeClosure(selectedEdge.id, c.id)} className="text-white/40 hover:text-rose-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <JsonFlagsEditor value={selectedEdge.flags} onChange={(v) => updateEdge(selectedEdge.id, { flags: v })} />
          <button type="button" onClick={deleteSelected} className="flex items-center gap-1 text-[10px] text-rose-400 hover:text-rose-300">
            <Trash2 className="h-3 w-3" /> Delete segment
          </button>
        </div>
      )}

      {/* Selected runway properties */}
      {selectedRunway && (
        <div className="space-y-1.5 rounded border border-slate-300/30 bg-slate-300/5 p-2">
          <div className="flex items-center justify-between">
            <div className={sectionTitle}>Runway · {selectedRunway.name}</div>
            <span className={`rounded px-1.5 py-0.5 text-[10px] ${selectedRunway.active.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/40'}`}>
              {selectedRunway.active.enabled ? 'ACTIVE' : 'OFF'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <div className={label}>Name</div>
              <input className={field} value={selectedRunway.name} onChange={(e) => updateRunway(selectedRunway.id, { name: e.target.value })} />
            </div>
            <div>
              <div className={label}>Width (ft)</div>
              <input type="number" className={field} value={selectedRunway.widthFt} onChange={(e) => updateRunway(selectedRunway.id, { widthFt: Number(e.target.value) || 0 })} />
            </div>
          </div>
          <div>
            <div className={label}>Surface</div>
            <input className={field} value={selectedRunway.surface ?? ''} placeholder="asphalt / concrete" onChange={(e) => updateRunway(selectedRunway.id, { surface: e.target.value || undefined })} />
          </div>
          {!selectedRunway.active.enabled && (
            <button
              type="button"
              onClick={() => setActiveRunway(selectedRunway.id)}
              className="w-full rounded bg-emerald-500/20 px-2 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/30"
            >
              Make active runway
            </button>
          )}
          {/* Per-end landing / takeoff config */}
          {[0, 1].map((i) => {
            const end = selectedRunway.ends[i as 0 | 1]
            return (
              <div key={i} className="space-y-1 rounded border border-white/10 bg-black/30 p-2">
                <div className="flex items-center gap-1.5">
                  <span className={label}>End</span>
                  <input
                    className={`${field} w-16`}
                    value={end.designator}
                    onChange={(e) => updateRunwayEnd(selectedRunway.id, i as 0 | 1, { designator: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 text-xs text-emerald-300">
                    <input
                      type="checkbox"
                      checked={end.useForLanding}
                      onChange={(e) => updateRunwayEnd(selectedRunway.id, i as 0 | 1, { useForLanding: e.target.checked })}
                    />
                    Landing
                  </label>
                  <label className="flex items-center gap-1 text-xs text-amber-300">
                    <input
                      type="checkbox"
                      checked={end.useForTakeoff}
                      onChange={(e) => updateRunwayEnd(selectedRunway.id, i as 0 | 1, { useForTakeoff: e.target.checked })}
                    />
                    Takeoff
                  </label>
                </div>
              </div>
            )
          })}
          <ActiveWindowEditor value={selectedRunway.active} onChange={(w) => updateRunway(selectedRunway.id, { active: w })} />
          <button type="button" onClick={deleteSelected} className="flex items-center gap-1 text-[10px] text-rose-400 hover:text-rose-300">
            <Trash2 className="h-3 w-3" /> Delete runway
          </button>
        </div>
      )}

      {/* Distribution */}
      {distribution.length > 0 && (
        <div className="space-y-1">
          <div className={sectionTitle}>Departure distribution</div>
          {distribution.map(({ node, count }) => (
            <div key={node.id} className="flex items-center justify-between rounded bg-black/30 px-2 py-1 text-xs">
              <span className="text-white/70">
                {node.flags?.name ? String(node.flags.name) : node.id.slice(0, 8)}
              </span>
              <span className="font-mono text-orange-300">
                {count}
                {node.throughputPerSlot ? ` / ${node.throughputPerSlot}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
